import { App } from '@capacitor/app'

import { tutorLlmEndpoint } from '../constants/llm-endpoint'
import { speechApiBaseUrl, speechApiKey, speechModel } from '../constants/speech-endpoint'

/**
 * Shortest gap between warm-ups.
 *
 * `resume` fires every time the tablet comes back from the lock screen, which
 * in a writing lesson is often. Warming a model that is already resident costs
 * a round trip and nothing else, but there is no reason to pay it every minute.
 */
const minimumIntervalMs = 60_000

/**
 * How long to wait before giving up.
 *
 * A cold model takes about fifteen seconds to load, so this has to outlast
 * that. Nothing waits on the result, so a slow warm-up costs the student
 * nothing either way.
 */
const timeoutMs = 60_000

let lastWarmedAt = 0
let inFlight = false

/**
 * Loads the models before a student needs them.
 *
 * The tutor's endpoints keep a model resident indefinitely once something has
 * asked for it, so the whole cost is paid by whichever request arrives first
 * after a server restart — about fifteen seconds. Left alone, that request is
 * the one a student triggers by finishing a paragraph, which is the worst
 * possible moment: they have stopped writing and are waiting to be told
 * something.
 *
 * Moving it to app start and to `resume` spends the same time while the app is
 * opening, where waiting is expected.
 *
 * Deliberately fire-and-forget. Warming is an optimisation; a failure here must
 * never delay or block the lesson, so errors are logged and dropped.
 */
export async function warmUpModels(reason: string): Promise<void> {
  const now = Date.now()
  if (inFlight || now - lastWarmedAt < minimumIntervalMs)
    return

  console.warn(`[aituter] warm-up starting (${reason})`)

  inFlight = true
  lastWarmedAt = now

  try {
    // Run both together: they are different machines' worth of work and neither
    // depends on the other.
    await Promise.allSettled([warmLlm(), warmSpeech()])
    console.warn(`[aituter] warmed models (${reason})`)
  }
  finally {
    inFlight = false
  }
}

async function warmLlm(): Promise<void> {
  // NOTICE:
  // No `keep_alive` field. The proxy injects `-1` itself, and a value sent by
  // the client wins over that injection — so passing even a generous number
  // here would replace "resident indefinitely" with a countdown.
  // Source/context: confirmed against dev.brainsp.com; /api/ps reports an
  // expiry in the year 2318 after any request through the proxy.
  // Removal condition: the proxy stops injecting it.
  await post(`${tutorLlmEndpoint.baseUrl}chat/completions`, tutorLlmEndpoint.apiKey, {
    model: tutorLlmEndpoint.model,
    reasoning_effort: 'none',
    max_tokens: 1,
    messages: [{ role: 'user', content: 'ping' }],
  })
}

async function warmSpeech(): Promise<void> {
  // One syllable: enough to make the synthesiser load its weights, short enough
  // that the audio it produces and we discard costs almost no GPU time.
  await post(`${speechApiBaseUrl}/v1/audio/speech`, speechApiKey, {
    model: speechModel,
    input: '네',
  })
}

async function post(url: string, apiKey: string, body: unknown): Promise<void> {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), timeoutMs)

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(apiKey ? { Authorization: `Bearer ${apiKey}` } : {}),
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    })

    // Drain the body so the connection is released rather than left open with
    // an unread response.
    await response.arrayBuffer().catch(() => undefined)
  }
  catch (error) {
    console.warn(`[aituter] warm-up failed for ${url}:`, error)
  }
  finally {
    clearTimeout(timeout)
  }
}

/**
 * Warms on launch, and again whenever the app returns to the foreground.
 *
 * A tablet left on a desk between paragraphs suspends the app; coming back is
 * exactly when the next question is about to be asked.
 */
export function installWarmUp(): void {
  void warmUpModels('launch')

  void App.addListener('resume', () => {
    void warmUpModels('resume')
  })
}
